/**
 * Society directory — wings summary card + society contact cards with call
 * rows, and a jump to the full resident directory. Wired: GET
 * /api/account/directory. Port of screens-account.jsx SocietyDirectory
 * (contacts side) per brief.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { num, str } from '@/components/gate/api';
import { CallIconButton, goBack, IconCircle } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useT, type TFunc } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, Hairline, Screen, SectionLabel, Skeleton, TopBar } from '@/ui';

type Contact = { id: string; name: string; role: string; phone?: string };
// counts kept raw; meta string built at render so "flats"/"residents" localise.
type Wing = { name: string; flats?: number; residents?: number };

function wingMeta(t: TFunc, w: Wing): string {
  return [
    w.flats !== undefined ? t('dir_flats', { n: w.flats }) : '',
    w.residents !== undefined ? t('dir_residents', { n: w.residents }) : '',
  ].filter(Boolean).join(' · ');
}

function parseBody(body: unknown): { contacts: Contact[]; wings: Wing[] } {
  const src = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const contactsRaw = Array.isArray(src.contacts) ? src.contacts
    : Array.isArray(src.staff) ? src.staff : [];
  const wingsRaw = Array.isArray(src.wings) ? src.wings : [];

  const contacts: Contact[] = contactsRaw.map((c, i) => ({
    id: str(c, 'id') ?? `c${i}`,
    name: str(c, 'name', 'title') ?? 'Contact',
    role: str(c, 'role', 'label', 'category') ?? '',
    phone: str(c, 'phone', 'phoneNumber'),
  }));

  const wings: Wing[] = wingsRaw.map((w, i) => ({
    name: str(w, 'name', 'wing', 'code') ?? `Wing ${i + 1}`,
    flats: num(w, 'flats', 'flatCount'),
    residents: num(w, 'residents', 'residentCount'),
  }));

  return { contacts, wings };
}

export default function SocietyDirectoryScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [data, setData] = React.useState<{ contacts: Contact[]; wings: Wing[] } | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await api.get<unknown>('/api/account/directory');
        if (cancelled) return;
        setData(parseBody(body));
        setState('ready');
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const empty = !data || (data.contacts.length === 0 && data.wings.length === 0);

  return (
    <Screen scroll={false}>
      <TopBar title={t('svc_directory')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            <Skeleton h={120} />
            {[0, 1, 2].map((i) => <Skeleton key={i} h={72} />)}
          </View>
        ) : state === 'error' || empty ? (
          <EmptyState
            icon={<Icon name="building" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? t('res_err_title') : t('dir_empty_title')}
            body={state === 'error' ? t('dir_err_body') : t('dir_empty_body')}
          />
        ) : (
          <>
            {data.wings.length > 0 ? (
              <>
                <SectionLabel>{t('dir_wings')}</SectionLabel>
                <Card style={{ paddingVertical: space.s1, marginBottom: space.s5 }}>
                  {data.wings.map((w, i) => {
                    const meta = wingMeta(t, w);
                    return (
                    <View key={w.name}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: space.s3 }}>
                        <AppText variant="bodyMd" weight="500">{w.name}</AppText>
                        {meta ? <AppText variant="bodySm" tone="tertiary">{meta}</AppText> : null}
                      </View>
                      {i < data.wings.length - 1 ? <Hairline /> : null}
                    </View>
                    );
                  })}
                </Card>
              </>
            ) : null}

            {data.contacts.length > 0 ? (
              <>
                <SectionLabel>{t('dir_contacts')}</SectionLabel>
                <View style={{ gap: space.s2 }}>
                  {data.contacts.map((c) => (
                    <Card key={c.id}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                        <IconCircle name="building" />
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <AppText variant="bodyMd" weight="500">{c.name}</AppText>
                          {c.role ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{c.role}</AppText> : null}
                        </View>
                        {c.phone ? <CallIconButton phone={c.phone} name={c.name} /> : null}
                      </View>
                    </Card>
                  ))}
                </View>
              </>
            ) : null}

            <Button
              title={t('dir_resident_dir')}
              kind="secondary"
              style={{ marginTop: space.s5 }}
              onPress={() => router.push('/residents')}
            />
          </>
        )}
      </Screen>
    </Screen>
  );
}
